// src/contexts/AuthContext.js
//
// Session state for the whole app (audit FL-03, FL-36):
// * the stored token is verified ONCE when the app starts, not on every navigation;
// * no token / rejected token -> user is null (the route guards show the login page);
// * a network error keeps the cached user, so a flaky connection does not log anyone out;
// * any request that gets 401 fires `auth:expired` (services/api.js) and we log out
//   with a notice instead of looping between /login and /.
import React, { createContext, useCallback, useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useMeta } from './MetaContext';
import api from '../services/api';

// The admin's own session while they look at the app as a group ("Zobrazit jako skupina").
const ADMIN_SESSION_KEY = 'adminSession';

function readAdminSession() {
  try {
    const raw = localStorage.getItem(ADMIN_SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && parsed.token && parsed.user ? parsed : null;
  } catch (e) {
    return null;
  }
}

function clearAdminSession() {
  try {
    localStorage.removeItem(ADMIN_SESSION_KEY);
  } catch (e) {
    /* ignore */
  }
}

export const AuthContext = createContext(null);

function cachedUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Verify the stored token once on start.
  useEffect(() => {
    let cancelled = false;
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      const result = await authService.verifyToken();
      if (cancelled) return;
      if (result?.valid) {
        setUser(result.user);
      } else if (localStorage.getItem('token')) {
        // Network / server error: verifyToken keeps the token, so keep the cached login.
        setUser(cachedUser());
      } else {
        setUser(null); // token was rejected and removed
      }
      setIsLoading(false);
    };
    initializeAuth();
    return () => {
      cancelled = true;
    };
  }, []);

  // Any 401 from the API means the session is over (a "view as group" session ends by
  // returning to the admin's own session instead).
  useEffect(() => {
    const onExpired = () => {
      if (!localStorage.getItem('token') && !user) return;
      const admin = readAdminSession();
      if (admin && user?.impersonatedBy) {
        clearAdminSession();
        authService.setAuthData(admin);
        setUser(admin.user);
        navigate('/admin/groups', { replace: true });
        return;
      }
      clearAdminSession();
      authService.logout();
      setUser(null);
      setSessionExpired(true);
      if (location.pathname !== '/login') {
        navigate('/login', { replace: true, state: { from: location.pathname, expired: true } });
      }
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, [user, navigate, location.pathname]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await authService.login(email, password);
      setUser(response.user);
      setSessionExpired(false);
      return { success: true, role: response.user.role };
    } catch (error) {
      return { success: false, status: error.status || null, message: error.message };
    }
  }, []);

  /** Store a session obtained elsewhere (e.g. right after group registration). */
  const startSession = useCallback((token, sessionUser) => {
    authService.setAuthData({ token, user: sessionUser });
    setUser(sessionUser);
    setSessionExpired(false);
  }, []);

  const logout = useCallback(() => {
    clearAdminSession();
    authService.logout();
    setUser(null);
    navigate('/login');
  }, [navigate]);

  /** Admin: continue as the group (short leader session); the admin session is kept aside. */
  const startImpersonation = useCallback(
    async (groupId) => {
      const { data } = await api.post(`/admin/groups/${groupId}/impersonate`);
      const adminToken = localStorage.getItem('token');
      localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({ token: adminToken, user, groupId }));
      authService.setAuthData({ token: data.token, user: data.user });
      setUser(data.user);
      navigate('/', { replace: true });
    },
    [navigate, user]
  );

  /** Back to the admin's own session. */
  const stopImpersonation = useCallback(async () => {
    const admin = readAdminSession();
    clearAdminSession();
    if (!admin) {
      authService.logout();
      setUser(null);
      navigate('/login');
      return;
    }
    authService.setAuthData({ token: admin.token, user: admin.user });
    setUser(admin.user);
    navigate(admin.groupId ? `/admin/groups/${admin.groupId}` : '/admin', { replace: true });
  }, [navigate]);

  // Who is logged in decides some features (our preview accounts): reload /meta after
  // login, logout and switching to / from "Zobrazit jako skupina".
  const { reload: reloadMeta } = useMeta();
  const sessionKey = user ? `${user.id}:${user.role}:${user.impersonatedBy ? 'as-group' : ''}` : 'anonymous';
  const firstSession = useRef(true);
  useEffect(() => {
    if (firstSession.current) {
      firstSession.current = false;
      return;
    }
    reloadMeta();
  }, [sessionKey, reloadMeta]);

  const value = {
    user,
    login,
    logout,
    startSession,
    startImpersonation,
    stopImpersonation,
    isLoading,
    sessionExpired,
  };

  return (
    <AuthContext.Provider value={value}>
      {isLoading ? (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="flex flex-col items-center gap-3 text-gray-600">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
            <span>Načítání…</span>
          </div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
